import React, { useState, useEffect } from 'react';
import { Bell, BarChart3, Info } from 'lucide-react';
import { useUserSettingsContext } from '../../context/UserSettingsContext';
import { api } from '../../utils/api';
import API_ENDPOINTS from '../../config/apiConfig';
import { toast } from 'react-hot-toast';

const Toggle = ({ enabled, onToggle, disabled }) => (
  <button
    onClick={onToggle}
    disabled={disabled}
    className="relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
    style={{
      backgroundColor: enabled ? 'var(--lodge-orange-400)' : 'var(--lodge-onyx-200)',
    }}
  >
    <div
      className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200"
      style={{ transform: enabled ? 'translateX(22px)' : 'translateX(2px)' }}
    />
  </button>
);

const EmailNotificationsSettings = () => {
  const { userSettings, fetchSettings } = useUserSettingsContext();
  const [saving, setSaving] = useState(false);
  const [mentionEmails, setMentionEmails] = useState(true);
  const [dashboardAlertEmails, setDashboardAlertEmails] = useState(true);

  useEffect(() => {
    if (userSettings) {
      const prefs = userSettings.emailNotificationPreferences || {};
      setMentionEmails(prefs.mentionEmails !== false);
      setDashboardAlertEmails(prefs.dashboardAlertEmails !== false);
    }
  }, [userSettings]);

  const handleToggle = async (type, newValue) => {
    if (saving) return;
    setSaving(true);

    const prev = type === 'mention' ? mentionEmails : dashboardAlertEmails;
    if (type === 'mention') setMentionEmails(newValue);
    else setDashboardAlertEmails(newValue);

    try {
      const updatedPreferences = {
        ...(userSettings.emailNotificationPreferences || {}),
        [type === 'mention' ? 'mentionEmails' : 'dashboardAlertEmails']: newValue,
      };

      await api.patch(API_ENDPOINTS.USER_SETTINGS, {
        ...userSettings,
        id: userSettings.email,
        emailNotificationPreferences: updatedPreferences,
      });

      await fetchSettings();
      toast.success('Preferences saved');
    } catch (err) {
      console.error('Error updating notification preferences:', err);
      if (type === 'mention') setMentionEmails(prev);
      else setDashboardAlertEmails(prev);
      toast.error('Failed to update preferences');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 style={{ fontFamily: 'var(--font-sans)', fontSize: 22, fontWeight: 700, color: 'var(--lodge-onyx-900)' }}>
          Notifications
        </h1>
        <p style={{ fontSize: 14, color: 'var(--lodge-onyx-500)', marginTop: 4 }}>
          Choose when you receive email alerts
        </p>
      </div>

      <section
        style={{
          borderRadius: 'var(--radius-xl)',
          background: 'var(--lodge-white)',
          border: '1px solid var(--lodge-onyx-100)',
          boxShadow: 'var(--shadow-sm)',
          overflow: 'hidden',
        }}
      >
        {/* Mention Emails */}
        <div className="flex items-center justify-between p-5" style={{ borderBottom: '1px solid var(--lodge-onyx-100)' }}>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="p-2 rounded-lg shrink-0" style={{ background: 'var(--lodge-orange-50)' }}>
              <Bell size={16} style={{ color: 'var(--lodge-orange-500)' }} />
            </div>
            <div className="min-w-0">
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--lodge-onyx-900)' }}>Note Mentions</div>
              <div style={{ fontSize: 12, color: 'var(--lodge-onyx-400)', marginTop: 1 }}>
                Get emailed when someone mentions you in a note
              </div>
            </div>
          </div>
          <Toggle enabled={mentionEmails} onToggle={() => handleToggle('mention', !mentionEmails)} disabled={saving} />
        </div>

        {/* Dashboard Alerts */}
        <div className="flex items-center justify-between p-5">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="p-2 rounded-lg shrink-0" style={{ background: 'var(--color-info-bg)' }}>
              <BarChart3 size={16} style={{ color: 'var(--color-info)' }} />
            </div>
            <div className="min-w-0">
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--lodge-onyx-900)' }}>Dashboard Alerts</div>
              <div style={{ fontSize: 12, color: 'var(--lodge-onyx-400)', marginTop: 1 }}>
                Get emailed about anomalies and important metric changes
              </div>
            </div>
          </div>
          <Toggle enabled={dashboardAlertEmails} onToggle={() => handleToggle('dashboardAlert', !dashboardAlertEmails)} disabled={saving} />
        </div>
      </section>

      {/* Info */}
      <div className="flex items-start gap-3 px-1" style={{ fontSize: 12, color: 'var(--lodge-onyx-400)' }}>
        <Info size={14} className="shrink-0 mt-0.5" />
        <span>Emails are only sent when you're not actively using the app. If you see the notification in-app, no email is sent.</span>
      </div>
    </div>
  );
};

export default EmailNotificationsSettings;
